import { useState, useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAppDispatch, useAppSelector } from 'src/store'
import { checkExistsUserTreatment } from 'src/useCase/userTreatment'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PATH as PATH_AUTH } from 'src/pages/auth'
import LocalStorage from 'src/utils/LocalStorage'
import useCreateTreatment from 'src/hook/useCreateTreatment'
import { getClinicDetail } from 'src/useCase/clinic'
import setNotificationActions from 'src/store/ui/notification'
import { updateUserInfo } from 'src/useCase/user'
import {
  getStoredSelectedGender,
  hasEffectiveGender,
  hasUserProfileGender,
  resolveEffectiveGender,
} from 'src/utils/gender'
import { trackStep, JOURNEY_STEP } from 'src/utils/tracker'

export type FormValues = {
  menu_id: string | number | null
  plan_id?: string | number | null
}

export const useMedicalSpecialtyForm = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const choicesOfUser = useAppSelector(
    (state) => state.domain.choicesUser.choicesUser,
  )
  const authInfo = useAppSelector((state) => state.ui.app.authInfo)
  const from = choicesOfUser?.from || searchParams.get('from') || 'LP'
  const uuid = searchParams.get('uuid')
  const accessToken = LocalStorage.accessToken
  const defaultValues: FormValues = {
    menu_id: null,
    plan_id: null,
  }
  const { createTreatment } = useCreateTreatment()
  const [isTelemedicine, setIsTelemedicine] = useState(false)
  const isDifferentClinic = LocalStorage.getDifferentClinicFlag()

  useEffect(() => {
    trackStep(JOURNEY_STEP.MENU_VIEWED)
  }, [])

  useEffect(() => {
    if (!isDifferentClinic) return

    dispatch(
      setNotificationActions.onNotification({
        type: 'warning',
        message: '選択された店舗（サロン）が前回と異なります。ご確認ください。',
      }),
    )

    LocalStorage.removeDifferentClinicFlag()
  }, [dispatch])

  const validationSchema = Yup.object().shape({
    menu_id: Yup.string().required('入力してください。').nullable(),
  })

  const userTreatmentForm = useForm<FormValues>({
    defaultValues,
  })

  const getTelemedicineStatus = useCallback(async () => {
    if (!uuid) return false
    try {
      const result = await dispatch(getClinicDetail({ uuid })).unwrap()
      const telemedicineEnabled = Boolean(result?.telemedicine_enabled)
      setIsTelemedicine(telemedicineEnabled)
      return telemedicineEnabled
    } catch {
      return false
    }
  }, [uuid, dispatch])

  useEffect(() => {
    getTelemedicineStatus()
  }, [getTelemedicineStatus])

  useEffect(() => {
    if (isTelemedicine) {
      LocalStorage.setTelemedicine('true')
    } else {
      LocalStorage.removeTelemedicine()
    }
  }, [isTelemedicine])

  const updateGenderProfile = useCallback(
    async (genderStr: string) => {
      if (!accessToken || !authInfo || hasUserProfileGender(authInfo.gender_id))
        return
      try {
        // After profile update, the effect watching `authInfo.gender_id` reloads
        // menus — avoid calling getMenus here to prevent duplicate requests.
        await dispatch(updateUserInfo({ gender_id: genderStr })).unwrap()
      } catch {
        dispatch(
          setNotificationActions.onNotification({
            type: 'error',
            message: '性別の更新に失敗しました。もう一度お試しください。',
          }),
        )
      }
    },
    [accessToken, authInfo, dispatch],
  )

  const onSubmit = useCallback(
    async (data: FormValues) => {
      // Use authInfo (same store as MedicalSpecialtyForm). updateUserInfo syncs it
      // after profile changes; localStorage covers the gap while the API is in flight.
      const genderFromUrl =
        searchParams.get('gender') || searchParams.get('gender_id')
      const genderSources = {
        userGenderId: authInfo?.gender_id,
        genderFromUrl,
        selectedGenderFromStorage: getStoredSelectedGender(),
      }

      if (!hasEffectiveGender(genderSources)) {
        dispatch(
          setNotificationActions.onNotification({
            type: 'error',
            message: '性別を選択してください。',
          }),
        )
        return
      }

      const effectiveGender = resolveEffectiveGender(genderSources)

      const queryParams: Record<string, string> = {
        uuid: searchParams.get('uuid') || '',
        clinicId: searchParams.get('clinicId') || '',
        menuId: String(data.menu_id || ''),
        from,
        telemedicine: isTelemedicine ? 'true' : 'false',
      }

      if (!accessToken) {
        queryParams.gender = effectiveGender
      }

      const query = new URLSearchParams(queryParams).toString()

      if (!accessToken) {
        LocalStorage.setTelemedicine(isTelemedicine ? 'true' : 'false')

        const targetPath = isTelemedicine
          ? PATH_AUTH.REGISTER
          : PATH_AUTH.PRECAUTION_MODAL_ONLINE

        trackStep(JOURNEY_STEP.MENU_SELECTED, {
          metadata: { menu_id: data.menu_id },
        })
        return navigate(`${targetPath}?${query}`)
      }

      try {
        const action = await dispatch(
          checkExistsUserTreatment({ menu_id: data.menu_id! }),
        )

        if (checkExistsUserTreatment.fulfilled.match(action)) {
          const planIdParam = searchParams.get('planId')
          const planId = planIdParam ? Number(planIdParam) : null
          const menuIdNum = data.menu_id ? Number(data.menu_id) : 0

          await createTreatment(menuIdNum, planId, userTreatmentForm)

          trackStep(JOURNEY_STEP.MENU_SELECTED, {
            metadata: { menu_id: data.menu_id },
          })
          return navigate(`${PATH_AUTH.PRECAUTION_MODAL_ONLINE}?${query}`)
        }
      } catch {
        return
      }
    },
    [
      searchParams,
      authInfo,
      dispatch,
      navigate,
      isTelemedicine,
      from,
      accessToken,
      createTreatment,
      userTreatmentForm,
    ],
  )

  const form = useForm({
    defaultValues,
    shouldUnregister: false,
    shouldFocusError: false,
    mode: 'all',
    resolver: yupResolver(validationSchema),
  })

  return {
    form,
    onSubmit,
    updateGenderProfile,
  }
}
